
const CSS_POSITION_ABSOLUTE = "absolute";
const CSS_POSITION_FIXED = "fixed";
const CSS_OVERFLOW_HIDDEN = "hidden";

/**
 * Blätter, die nur bei Überlauf gelten.
 *
 * Ob ein Schreiben eine zweite Seite braucht, steht erst fest, wenn der Text gesetzt ist — und
 * gesetzt wird er im Browser. Der Server liefert das zweite Blatt deshalb immer mit und markiert
 * es nur als bedingt; hier wird gemessen, gesetzt und auf dem Folgesatzspiegel fortgesetzt.
 */

/** Muss zu `ConditionalSheetAttribute` im `SystemDocumentRenderService` passen. */
const CONDITIONAL_SHEET_ATTRIBUTE = "data-mq-sheet-when-overflow";

/** Muss zu `ConditionalSheetVisibleAttribute` im `SystemDocumentRenderService` passen. */
const OVERFLOWING_ATTRIBUTE = "data-mq-sheet-overflowing";

/** Rundungen aus Millimetern dürfen nicht selbst eine Seite erzeugen. */
const OVERFLOW_TOLERANCE_PX = 1;

/** Bereiche am Blattfuß, die den Satzspiegel nach unten begrenzen. */
const ANCHORED_SELECTORS = [".mq-preview-bottom", ".mq-preview-footer", ".mq-preview-page-number"];

/**
 * Der ursprüngliche Inhalt eines Folgeseiten-Blatts steht im Server-Markup, weil das
 * Erscheinungsbild auch eine gestaltbare Folgeseite kennt. Ein Textsatz braucht daraus bei
 * Überlauf jedoch nur den Satzspiegel; sein Fließtext kommt fortlaufend von Blatt 1.
 *
 * Ein WeakMap statt eines data-Attributs: Der Inhalt kann groß sein und jede Attributänderung
 * würde wiederum den Beobachter der Vorschau auslösen.
 */
const PAGINATED_SOURCES = new WeakMap<HTMLElement, string>();

type FirstSheetGeometry = {
  /** Wo der Fließtext enden muss, bevor Fuß oder angeheftete Bausteine beginnen. */
  limit: number;
  /** Oberkante des Fließtexts — daraus folgt, wie viel auf die Folgeseite fortgesetzt wird. */
  contentTop: number;
  /** Unterkante des Fließtexts; für die reine Überlaufaussage. */
  contentBottom: number;
};

/**
 * Liefert den Beginn der ersten nicht mehr vollständig passenden Textzeile.
 *
 * Eine reine Höhe würde einen Absatz mitten durch eine Zeile schneiden: Das letzte Wort stünde
 * dann noch auf Seite 1 und zugleich auf Seite 2. Die Rechtecke eines Range kennt der Browser
 * dagegen für jede tatsächlich gesetzte Zeile — auch bei Umbruch, unterschiedlicher Schrift und
 * Inline-Auszeichnung. Genau an diesem Beginn darf der Folgesatz einsetzen.
 */
function continuationHeight(content: HTMLElement, contentTop: number, limit: number): number {
  const walker = content.ownerDocument.createTreeWalker(content, NodeFilter.SHOW_TEXT);

  for (let node = walker.nextNode(); node != null; node = walker.nextNode()) {
    if (node.textContent?.trim().length === 0) continue;

    const range = content.ownerDocument.createRange();
    range.selectNodeContents(node);
    for (const rect of Array.from(range.getClientRects())) {
      if (rect.bottom > limit + OVERFLOW_TOLERANCE_PX) {
        return Math.max(0, rect.top - contentTop);
      }
    }
  }

  // Ein leerer oder rein grafischer Baustein kann überlaufen, ohne ein Textrechteck zu liefern.
  // Er bleibt an der sicheren Blattgrenze; bei normalem Text ist der obige, zeilengenaue Pfad
  // maßgeblich.
  return Math.max(0, limit - contentTop);
}

/** Misst den verfügbaren Satzspiegel des ersten Blatts. */
function firstSheetGeometry(previewDocument: Document): FirstSheetGeometry | null {
  const view = previewDocument.defaultView;
  const sheet = previewDocument.querySelector<HTMLElement>(".mq-sheet-1");
  if (view == null || sheet == null) return null;

  const sheetRect = sheet.getBoundingClientRect();
  const sheetStyle = view.getComputedStyle(sheet);
  let limit = sheetRect.bottom - (parseFloat(sheetStyle.paddingBottom) || 0);

  for (const selector of ANCHORED_SELECTORS) {
    for (const anchored of Array.from(sheet.querySelectorAll<HTMLElement>(selector))) {
      limit = Math.min(limit, anchored.getBoundingClientRect().top);
    }
  }

  // Gemessen wird nur der Fluss. Fuß, verankerte Bausteine und Falzmarken sind absolut am Blatt.
  let contentBottom = sheetRect.top;
  for (const child of Array.from(sheet.children) as HTMLElement[]) {
    const position = view.getComputedStyle(child).position;
    if (position === CSS_POSITION_ABSOLUTE || position === CSS_POSITION_FIXED) continue;
    contentBottom = Math.max(contentBottom, child.getBoundingClientRect().bottom);
  }

  const content = sheet.querySelector<HTMLElement>(".mq-content");
  // Nach einem erkannten Überlauf wird der sichtbare Textkörper beschnitten. `scrollHeight` bleibt
  // dabei die natürliche Höhe und verhindert, dass die nächste Beobachtung die Folgeseite wieder
  // ausblendet, nur weil der sichtbare Ausschnitt jetzt exakt am Satzspiegel endet.
  if (content != null) {
    const contentRect = content.getBoundingClientRect();
    contentBottom = Math.max(contentBottom, contentRect.top + content.scrollHeight);
  }

  return {
    limit,
    contentTop: content?.getBoundingClientRect().top ?? sheetRect.top,
    contentBottom,
  };
}

/**
 * Setzt einen Textsatz bei Überlauf auf dem Folgesatzspiegel fort. Das Erscheinungsbild bleibt
 * unverändert gestaltbar: Nur ein als bedingt markiertes Blatt bekommt den fortlaufenden Inhalt.
 *
 * Der Browser kann keinen Druckseitenumbruch zwischen zwei unabhängigen Elementen verteilen. Die
 * zweite Seite zeigt deshalb denselben Fließtext um genau die Höhe des ersten Satzspiegels nach
 * oben versetzt und beschneidet ihn am Blatt. So bleiben Text, Fuß und Trennlinie getrennt, ohne
 * gespeicherten Text oder Bausteinreihenfolge zu verändern.
 */
export function applyDocumentSheetLayout(previewDocument: Document): void {
  const conditional = Array.from(
    previewDocument.querySelectorAll<HTMLElement>(`[${CONDITIONAL_SHEET_ATTRIBUTE}]`)
  );
  if (conditional.length === 0) return;

  const geometry = firstSheetGeometry(previewDocument);
  if (geometry == null) return;
  const overflowing = geometry.contentBottom > geometry.limit + OVERFLOW_TOLERANCE_PX;
  const firstContent = previewDocument.querySelector<HTMLElement>(".mq-sheet-1 .mq-content");

  for (const sheet of conditional) {
    if (!overflowing) {
      sheet.removeAttribute(OVERFLOWING_ATTRIBUTE);
      if (firstContent != null) {
        if (firstContent.style.maxHeight) firstContent.style.maxHeight = "";
        if (firstContent.style.overflow) firstContent.style.overflow = "";
      }
      continue;
    }

    // Ein unverändert neu gesetztes Attribut meldet dem Beobachter trotzdem eine Mutation und
    // würde die Messung selbst wieder auslösen.
    if (!sheet.hasAttribute(OVERFLOWING_ATTRIBUTE)) sheet.setAttribute(OVERFLOWING_ATTRIBUTE, "");
    const followingContent = sheet.querySelector<HTMLElement>(".mq-content");
    if (firstContent == null || followingContent == null) continue;

    // Blatt 1 endet am Anfang einer vollständigen, noch nicht passenden Zeile. Der Rest beginnt
    // sichtbar auf Blatt 2; damit kann er weder die Trennlinie noch den Fuß überdecken und die
    // letzte Zeile erscheint nicht auf beiden Blättern.
    const continuation = continuationHeight(firstContent, geometry.contentTop, geometry.limit);
    const visibleHeight = `${continuation}px`;
    if (firstContent.style.maxHeight !== visibleHeight) firstContent.style.maxHeight = visibleHeight;
    if (firstContent.style.overflow !== CSS_OVERFLOW_HIDDEN) firstContent.style.overflow = CSS_OVERFLOW_HIDDEN;

    const source = firstContent.innerHTML;
    if (PAGINATED_SOURCES.get(followingContent) !== source) {
      followingContent.innerHTML = source;
      PAGINATED_SOURCES.set(followingContent, source);
    }
    const transform = `translateY(-${continuation}px)`;
    if (followingContent.style.transform !== transform) followingContent.style.transform = transform;
  }
}
